/**
 * LiveGeoJSONLayer
 *
 * Implementation for live GeoJSON layers that fetch data from an API endpoint.
 * Supports automatic refresh and custom styling.
 *
 * Expected config structure:
 * {
 *   "dataUrl": "https://api.example.com/geojson",  // API endpoint
 *   "refreshInterval": 60000,                       // Auto-refresh interval in ms (optional)
 *   "headers": {                                    // Custom HTTP headers (optional)
 *     "Authorization": "Bearer token"
 *   },
 *   "style": {                                      // Mapbox GL style
 *     "type": "circle",                             // circle, line, fill, symbol
 *     "paint": { ... },                             // Paint properties
 *     "layout": { ... }                             // Layout properties
 *   },
 *   "cluster": {                                    // Clustering config (optional)
 *     "enabled": true,
 *     "radius": 50,
 *     "maxZoom": 14
 *   }
 * }
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { loadFaIconToMap, getIconId, getDefaultIcon, faIconToSvg } from './utils/iconUtils';
import { resolveStyleValue, renderHoverTemplate, FaMarkerStyle, resolveMarkerOffset, resolvePopupPlacement } from './utils/faMarkerStyle';
import { visibilityAwareInterval, VisibilityAwareIntervalHandle } from './utils/visibilityAwareInterval';
import { isInRefreshTimeRange } from './utils/refreshTimeRange';
import { getBackendUrl } from '../../services/configService';
import { isPerimeterShown } from '../perimeters/firePerimeterOverlay';
import {
  getSymbologyFromStyle,
  buildIconImageExpression,
  buildIconSizeExpression,
} from './layerSymbology';

export class LiveGeoJSONLayer extends BaseLayer {
  /**
   * Mapbox source ID for this layer
   */
  private sourceId: string;

  /**
   * Mapbox layer IDs for this layer (may have multiple for clusters)
   */
  private layerIds: string[];

  /**
   * GeoJSON data cache
   */
  private geojsonData: GeoJSON.FeatureCollection | null = null;

  /**
   * Auto-refresh handle (visibility-aware).
   */
  private refreshHandle: VisibilityAwareIntervalHandle | null = null;

  /**
   * Last fetch timestamp
   */
  private lastFetchTime: number = 0;

  /**
   * Current icon ID for symbol layers
   */
  private currentIconId: string | null = null;
  // #653: fire layers colour icons by wildfireStatus. One registered map image
  // id per status bucket, keyed by the bucket label (Being Monitored → yellow,
  // Being Held / Under Control → green, Out → black + smaller, Out of Control →
  // red). Empty for non-fire layers. Supersedes the #622 binary active/out pair.
  private iconIdByLabel: Record<string, string> = {};

  /**
   * Bound event handlers for cleanup
   */
  private contextMenuHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;

  /**
   * Per-feature DOM markers when style.type === 'fa-marker'
   */
  private domMarkers: mapboxgl.Marker[] = [];
  private faMarkerHoverPopup: mapboxgl.Popup | null = null;

  /**
   * Constructor
   *
   * @param map Mapbox GL map instance
   * @param config Layer configuration from database
   */
  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);
    this.sourceId = `live-geojson-source-${config.id}`;
    this.layerIds = [
      `live-geojson-layer-${config.id}`,
      `live-geojson-cluster-${config.id}`,
      `live-geojson-cluster-count-${config.id}`
    ];
  }

  /**
   * Initialize layer
   */
  protected async onInitialize(): Promise<void> {
    // Validate required config
    const dataUrl = this.config.config.dataUrl || this.config.source_url;
    if (!dataUrl) {
      throw new Error('LiveGeoJSONLayer requires config.dataUrl or source_url');
    }

    // Try to fetch initial data but don't fail if it's not available
    try {
      await this.fetchData();
    } catch (error) {
      console.warn(`LiveGeoJSONLayer: Initial data fetch failed for ${this.config.name}, will retry later:`, error);
      // Initialize with empty data
      this.geojsonData = { type: 'FeatureCollection', features: [] };
    }
  }

  /**
   * Enable layer on map
   */
  protected async onEnable(): Promise<void> {
    const config = this.config.config;
    const style = config.style || {};

    // fa-marker rendering uses per-feature DOM markers (mapboxgl.Marker
    // with inline FontAwesome SVG) instead of a mapbox circle/symbol
    // layer. Skip the source/layer setup entirely; markers are appended
    // directly to the map.
    if (style.type === 'fa-marker') {
      this.renderFaMarkers();
      const refreshInterval = config.refreshInterval;
      if (refreshInterval && refreshInterval > 0) {
        this.startAutoRefresh(refreshInterval);
      }
      return;
    }

    // Load icon if this is a symbol layer
    if (style.type === 'symbol') {
      const iconName = style.icon || getDefaultIcon(this.config.name);
      const iconColor = style.paint?.['icon-color'] || '#3b82f6';
      const iconSize = style.paint?.['icon-size'] || 1.5;
      // Halo/outline settings for accessibility (especially on satellite backgrounds)
      const iconHaloColor = style.haloColor || style.paint?.['icon-halo-color'];
      const iconHaloWidth = style.haloWidth ?? style.paint?.['icon-halo-width'] ?? 0;
      const iconShadowBlur = style.shadowBlur ?? 0;
      const iconShadowColor = style.shadowColor;

      try {
        this.currentIconId = await loadFaIconToMap(this.map, iconName, {
          color: iconColor,
          size: iconSize,
          haloColor: iconHaloColor,
          haloWidth: iconHaloWidth,
          shadowBlur: iconShadowBlur,
          shadowColor: iconShadowColor
        });
        console.log(`[LiveGeoJSONLayer] Loaded icon: ${this.currentIconId}`);

        // #653: fire layers carry a per-feature `wildfireStatus`. Colour the
        // icon by status bucket (Being Monitored → yellow, Being Held / Under
        // Control → green, Out → black + smaller, Out of Control → red). The
        // icon cache keys by (name, color, size), so registering one image per
        // bucket yields a distinct id each; buildSymbolLayout then emits a
        // `match` expression on wildfireStatus selecting per bucket.
        const fireIcons = __fireIconRegistrationsForTests(style);
        for (const reg of fireIcons) {
          this.iconIdByLabel[reg.label] = await loadFaIconToMap(this.map, iconName, {
            color: reg.color,
            size: reg.size,
            // #697: halo + shadow are per-bucket — only the black Out icon gets
            // a white outline; active fires render with a subtle grey drop
            // shadow instead.
            haloColor: reg.haloColor,
            haloWidth: reg.haloWidth,
            shadowBlur: reg.shadowBlur,
            shadowColor: reg.shadowColor
          });
        }
        if (fireIcons.length) {
          console.log(`[LiveGeoJSONLayer] Loaded ${fireIcons.length} status fire icons`);
        }
      } catch (error) {
        console.error(`[LiveGeoJSONLayer] Failed to load icon ${iconName}, falling back to circle:`, error);
        // Fall back to circle if icon fails
        this.config.config.style = { ...style, type: 'circle' };
      }
    }

    // Add source if it doesn't exist
    if (!this.map.getSource(this.sourceId)) {
      const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
        type: 'geojson',
        data: this.geojsonData || { type: 'FeatureCollection', features: [] }
      };

      // Add clustering if configured
      if (config.cluster?.enabled) {
        sourceConfig.cluster = true;
        sourceConfig.clusterRadius = config.cluster.radius || 50;
        sourceConfig.clusterMaxZoom = config.cluster.maxZoom || 14;

        if (config.cluster.clusterProperties) {
          sourceConfig.clusterProperties = config.cluster.clusterProperties;
        }
      }

      this.map.addSource(this.sourceId, sourceConfig);
    }

    // Add layers if they don't exist
    this.addLayers();

    // Wire up wildfire context menu if configured
    if (config.contextMenu === 'wildfire') {
      this.setupWildfireContextMenu();
    }

    // Stale-window guard: the user can leave a tab open for hours with this
    // layer off (so auto-refresh never started), then flip it on. The source
    // above carries whatever onInitialize captured at app load — possibly
    // empty or hours stale. Fire a fresh fetch; fetchData auto-pushes to the
    // source so the new data lands as soon as it arrives. Fire-and-forget so
    // the toggle UI stays snappy.
    this.refresh().catch(err =>
      console.warn(`[LiveGeoJSONLayer ${this.config.name}] onEnable refresh failed:`, err),
    );

    // Start auto-refresh if configured
    const refreshInterval = config.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      this.startAutoRefresh(refreshInterval);
    }
  }

  /**
   * Disable layer on map
   */
  protected async onDisable(): Promise<void> {
    // Tear down per-feature DOM markers (fa-marker style)
    this.clearFaMarkers();

    // Hide all layers
    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', 'none');
      }
    });

    // Stop auto-refresh
    this.stopAutoRefresh();

    // Clean up context menu handlers
    this.cleanupWildfireContextMenu();
  }

  /**
   * Update layer opacity
   *
   * @param opacity Opacity value (0-1)
   */
  protected async onSetOpacity(opacity: number): Promise<void> {
    const mainLayerId = this.layerIds[0];
    if (!this.map.getLayer(mainLayerId)) return;

    const layerType = this.config.config.style?.type || 'circle';

    // Update opacity based on layer type
    switch (layerType) {
      case 'circle':
        this.map.setPaintProperty(mainLayerId, 'circle-opacity', opacity);
        break;
      case 'line':
        this.map.setPaintProperty(mainLayerId, 'line-opacity', opacity);
        break;
      case 'fill':
        this.map.setPaintProperty(mainLayerId, 'fill-opacity', opacity);
        break;
      case 'symbol':
        this.map.setPaintProperty(mainLayerId, 'icon-opacity', opacity);
        this.map.setPaintProperty(mainLayerId, 'text-opacity', opacity);
        break;
    }

    // Update cluster layer opacity if exists
    const clusterLayerId = this.layerIds[1];
    if (this.map.getLayer(clusterLayerId)) {
      this.map.setPaintProperty(clusterLayerId, 'circle-opacity', opacity);
    }
  }

  /**
   * Update layer order
   *
   * @param order Order value (higher = on top)
   */
  protected onSetOrder(order: number): void {
    // Move all layers to appropriate position
    // Implementation similar to other layer types
  }

  /**
   * Destroy layer and clean up resources
   */
  protected async onDestroy(): Promise<void> {
    // Stop auto-refresh
    this.stopAutoRefresh();

    // Tear down per-feature DOM markers (fa-marker style)
    this.clearFaMarkers();

    // Clean up context menu handlers
    this.cleanupWildfireContextMenu();

    // Remove all layers
    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });

    // Remove source
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    // Clear data cache
    this.geojsonData = null;
  }

  /**
   * Get Mapbox layer IDs
   */
  public getMapboxLayerIds(): string[] {
    return this.layerIds.filter(layerId => this.map.getLayer(layerId));
  }

  /**
   * Get Mapbox source IDs
   */
  public getMapboxSourceIds(): string[] {
    return [this.sourceId];
  }

  /**
   * Manually refresh layer data
   */
  public async refresh(): Promise<void> {
    // Guard against the auto-refresh timer firing during basemap swaps or
    // layer teardown — map.getSource() can hit an undefined style manager
    // and throw (seen as "Cannot read properties of undefined (reading
    // 'getOwnSource')").
    if (!this.state.isEnabled) return;
    if (!this.map || !this.map.isStyleLoaded()) return;

    await this.fetchData();

    if (!this.state.isEnabled || !this.map?.isStyleLoaded()) return;

    const style = this.config.config.style || {};
    if (style.type === 'fa-marker') {
      // fa-marker uses no mapbox source — re-render the DOM markers in place.
      this.renderFaMarkers();
      return;
    }

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source && this.geojsonData) {
      source.setData(this.geojsonData);
    }
  }

  /**
   * Get last fetch timestamp
   */
  public getLastFetchTime(): number {
    return this.lastFetchTime;
  }

  /**
   * Fetch data from API endpoint
   */
  private async fetchData(): Promise<void> {
    // Get backend URL for production/staging (where frontend and backend are on different domains)
    const backendUrl = getBackendUrl() || '';

    let dataUrl = this.config.config.dataUrl || this.config.source_url;
    const headers = this.config.config.headers || {};

    // CRITICAL: In production/staging, prepend backend URL to relative paths
    // This fixes the issue where relative URLs resolve to frontend origin instead of backend
    if (dataUrl.startsWith('/') && backendUrl) {
      dataUrl = backendUrl + dataUrl;
      console.log(`[LiveGeoJSONLayer] Prepended backendUrl: ${dataUrl}`);
    }

    try {
      const response = await fetch(dataUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle different response formats
      let geojson = data;

      // Format 1: API returns { success: true, data: {...} }
      if (data.success && data.data) {
        geojson = data.data;
      }

      // Format 2: API returns array, convert to FeatureCollection
      if (Array.isArray(geojson)) {
        geojson = {
          type: 'FeatureCollection',
          features: geojson.map((item: any) => {
            // If item is already a Feature, use it as-is
            if (item.type === 'Feature') {
              return item;
            }
            // Otherwise, construct a Feature from the item
            return {
              type: 'Feature',
              geometry: item.geometry || {
                type: 'Point',
                coordinates: [item.lon || item.longitude || 0, item.lat || item.latitude || 0]
              },
              properties: item
            };
          })
        };
      }

      // Validate final GeoJSON structure
      if (!geojson || geojson.type !== 'FeatureCollection') {
        console.error('Invalid GeoJSON response:', geojson);
        throw new Error('Invalid GeoJSON response from API - expected FeatureCollection');
      }

      this.geojsonData = geojson;
      this.lastFetchTime = Date.now();

      // Sync to the live source if it's already on the map. onInitialize's
      // fetch can resolve AFTER onEnable adds the source with empty data —
      // without this, the layer would render empty until the next auto-
      // refresh tick (the "toggle on → nothing → a few seconds later it
      // shows up" race).
      const style = this.config.config.style || {};
      if (style.type !== 'fa-marker') {
        const source = this.map?.getSource(this.sourceId) as mapboxgl.GeoJSONSource | undefined;
        if (source) {
          source.setData(geojson);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to fetch live GeoJSON data: ${errorMessage}`);
    }
  }

  /**
   * Set up wildfire context menu (right-click → Details / Duty Log)
   */
  private setupWildfireContextMenu(): void {
    const mainLayerId = this.layerIds[0];

    this.contextMenuHandler = (e: mapboxgl.MapMouseEvent) => {
      const features = this.map.queryRenderedFeatures(e.point, { layers: [mainLayerId] });
      if (!features.length) return;

      e.preventDefault();
      const feature = features[0];
      const fireNumber = feature.properties?.number || 'Unknown';
      this.showWildfireContextMenu(e.lngLat, fireNumber);
    };

    this.clickHandler = (e: mapboxgl.MapMouseEvent) => {
      const features = this.map.queryRenderedFeatures(e.point, { layers: [mainLayerId] });
      if (!features.length) return;

      const feature = features[0];
      const fireNumber = feature.properties?.number || 'Unknown';
      window.dispatchEvent(new CustomEvent('show-wildfire-details', { detail: { fireNumber } }));
    };

    this.map.on('contextmenu', mainLayerId, this.contextMenuHandler);
    this.map.on('click', mainLayerId, this.clickHandler);

    // Change cursor on hover
    this.map.on('mouseenter', mainLayerId, () => { this.map.getCanvas().style.cursor = 'pointer'; });
    this.map.on('mouseleave', mainLayerId, () => { this.map.getCanvas().style.cursor = ''; });
  }

  /**
   * Show wildfire context menu popup
   */
  private showWildfireContextMenu(lngLat: mapboxgl.LngLat, fireNumber: string): void {
    // Remove any existing context menu
    document.querySelectorAll('.live-geojson-context-menu').forEach(el => el.remove());

    const point = this.map.project(lngLat);
    const menu = document.createElement('div');
    menu.className = 'context-menu live-geojson-context-menu';
    menu.style.position = 'absolute';
    menu.style.left = `${point.x}px`;
    menu.style.top = `${point.y}px`;

    const perimeterShown = isPerimeterShown({ fireNumber });
    const perimeterLabel = perimeterShown ? 'Hide Perimeter' : 'Show Perimeter';

    menu.innerHTML = `
      <h3>${fireNumber}</h3>
      <ul>
        <li class="ctx-details">Details</li>
        <li class="ctx-dutylog">Duty Log</li>
        <li class="ctx-perimeter">${perimeterLabel}</li>
      </ul>
    `;

    const mapContainer = this.map.getContainer();
    mapContainer.appendChild(menu);

    menu.querySelector('.ctx-details')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('show-wildfire-details', { detail: { fireNumber } }));
      menu.remove();
    });

    menu.querySelector('.ctx-dutylog')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('show-enhanced-dutylog-explorer', { detail: { fireNumber } }));
      menu.remove();
    });

    menu.querySelector('.ctx-perimeter')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('toggle-fire-perimeter', { detail: { fireNumber } }));
      menu.remove();
    });

    document.addEventListener('click', () => menu.remove(), { once: true });
  }

  /**
   * Clean up wildfire context menu handlers
   */
  private cleanupWildfireContextMenu(): void {
    const mainLayerId = this.layerIds[0];
    if (this.contextMenuHandler) {
      this.map.off('contextmenu', mainLayerId, this.contextMenuHandler);
      this.contextMenuHandler = null;
    }
    if (this.clickHandler) {
      this.map.off('click', mainLayerId, this.clickHandler);
      this.clickHandler = null;
    }
    document.querySelectorAll('.live-geojson-context-menu').forEach(el => el.remove());
  }

  /**
   * Add layers to map
   */
  private addLayers(): void {
    const config = this.config.config;
    const mainLayerId = this.layerIds[0];

    // Add main layer if it doesn't exist
    if (!this.map.getLayer(mainLayerId)) {
      const layerDef = this.buildLayerDefinition();
      this.map.addLayer(layerDef);
    } else {
      this.map.setLayoutProperty(mainLayerId, 'visibility', 'visible');
    }

    // Add cluster layers if clustering is enabled
    if (config.cluster?.enabled) {
      this.addClusterLayers();
    }
  }

  /**
   * Add cluster layers
   */
  private addClusterLayers(): void {
    const clusterLayerId = this.layerIds[1];
    const clusterCountLayerId = this.layerIds[2];

    // Cluster circle layer
    if (!this.map.getLayer(clusterLayerId)) {
      this.map.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: this.sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            100,
            '#f1f075',
            750,
            '#f28cb1'
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40
          ],
          'circle-opacity': this.state.currentOpacity
        }
      });
    }

    // Cluster count label layer
    if (!this.map.getLayer(clusterCountLayerId)) {
      this.map.addLayer({
        id: clusterCountLayerId,
        type: 'symbol',
        source: this.sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: {
          'text-color': '#ffffff'
        }
      });
    }
  }

  /**
   * Build layer definition
   */
  /**
   * Build the symbol layer's layout. For categorical layers (fire: Active/Out)
   * the icon-image/icon-size become Mapbox `match` expressions derived from the
   * shared symbology descriptor — the same descriptor the legend reads, so map
   * and legend never drift (#622).
   */
  private buildSymbolLayout(style: any): Record<string, unknown> {
    const symbology = getSymbologyFromStyle(style);
    if (symbology && Object.keys(this.iconIdByLabel).length) {
      // #653: select the registered coloured icon per status bucket.
      return __buildFireSymbolLayoutForTests(style, this.iconIdByLabel, this.currentIconId);
    }
    return __buildDefaultSymbolLayoutForTests(this.currentIconId, style.paint?.['icon-size'] || 1);
  }

  private buildLayerDefinition(): mapboxgl.AnyLayer {
    const config = this.config.config;
    const style = config.style || {};
    const layerType = style.type || 'circle';

    // Base layer definition
    const baseDef: Record<string, any> = {
      id: this.layerIds[0],
      type: layerType as any,
      source: this.sourceId,
      minzoom: this.config.zoom_min,
      maxzoom: this.config.zoom_max
    };

    // Add cluster filter if clustering is enabled
    if (config.cluster?.enabled) {
      baseDef['filter'] = ['!', ['has', 'point_count']];
    }

    // Default paint styles
    const defaultPaint: Record<string, any> = {
      circle: {
        'circle-radius': 6,
        'circle-color': '#3b82f6',
        'circle-opacity': this.state.currentOpacity,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      },
      line: {
        'line-color': '#3b82f6',
        'line-width': 2,
        'line-opacity': this.state.currentOpacity
      },
      fill: {
        'fill-color': '#3b82f6',
        'fill-opacity': this.state.currentOpacity * 0.5,
        'fill-outline-color': '#1e40af'
      },
      symbol: __buildDefaultSymbolPaintForTests(this.state.currentOpacity)
    };

    // Default layout styles by type
    const defaultLayout: Record<string, any> = {
      circle: { visibility: 'visible' },
      line: { visibility: 'visible' },
      fill: { visibility: 'visible' },
      symbol: this.buildSymbolLayout(style)
    };

    // Merge paint and layout. Note: DB-stored styles often include
    // layout-only properties (e.g. icon-size) under `paint` for symbol
    // layers. Strip those before applying so Mapbox doesn't warn about
    // unknown paint properties.
    const SYMBOL_LAYOUT_ONLY = ['icon-size', 'icon-image', 'icon-allow-overlap', 'icon-ignore-placement', 'text-field', 'text-size', 'text-font'];
    const stylePaint: Record<string, unknown> = { ...(style.paint || {}) };
    if (layerType === 'symbol') {
      for (const key of SYMBOL_LAYOUT_ONLY) {
        delete stylePaint[key];
      }
    }
    const paint = { ...defaultPaint[layerType], ...stylePaint };
    const layout = { ...defaultLayout[layerType], ...style.layout };

    return {
      ...baseDef,
      paint,
      layout
    } as mapboxgl.AnyLayer;
  }

  /**
   * Render per-feature DOM markers for the fa-marker style.
   * Removes any existing markers first (idempotent on refresh).
   */
  private renderFaMarkers(): void {
    this.clearFaMarkers();

    const style = (this.config.config.style || {}) as FaMarkerStyle;
    const features = this.geojsonData?.features || [];
    const iconName = style.icon || 'circle';

    for (const feature of features) {
      if (!feature.geometry || feature.geometry.type !== 'Point') continue;
      const coords = feature.geometry.coordinates as [number, number];
      const props = (feature.properties || {}) as Record<string, unknown>;

      const color = resolveStyleValue(style.color, '#3b82f6', props);
      const size = resolveStyleValue(style.size, 1.0, props);

      const svg = faIconToSvg(iconName, { color, size });
      if (!svg) continue;

      const el = document.createElement('div');
      el.className = 'live-geojson-fa-marker';
      el.style.cursor = 'pointer';
      el.style.lineHeight = '0';
      el.innerHTML = svg;

      if (style.hoverTemplate) {
        const template = style.hoverTemplate;
        el.addEventListener('mouseenter', () => {
          if (this.faMarkerHoverPopup) {
            this.faMarkerHoverPopup.remove();
          }
          this.faMarkerHoverPopup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'hover-popup live-geojson-fa-marker-popup',
            // #790 — out to the side of the marker, clear of anything centred
            // above the feature's own coordinates.
            ...resolvePopupPlacement(style),
          })
            .setLngLat(coords)
            .setHTML(renderHoverTemplate(template, props))
            .addTo(this.map);
        });
        el.addEventListener('mouseleave', () => {
          if (this.faMarkerHoverPopup) {
            this.faMarkerHoverPopup.remove();
            this.faMarkerHoverPopup = null;
          }
        });
      }

      // #790 — a layer may ask to be drawn clear of its own coordinates, so it
      // does not cover something else rendered there.
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
        offset: resolveMarkerOffset(style),
      })
        .setLngLat(coords)
        .addTo(this.map);
      this.domMarkers.push(marker);
    }
  }

  /**
   * Remove all DOM markers and any open hover popup.
   */
  private clearFaMarkers(): void {
    for (const m of this.domMarkers) m.remove();
    this.domMarkers = [];
    if (this.faMarkerHoverPopup) {
      this.faMarkerHoverPopup.remove();
      this.faMarkerHoverPopup = null;
    }
  }

  /**
   * Start auto-refresh timer. Visibility-aware (pauses in background tabs).
   * Gated by refreshTimeRange when configured.
   */
  private startAutoRefresh(interval: number): void {
    this.stopAutoRefresh();

    this.refreshHandle = visibilityAwareInterval(() => {
      const range = (this.config.config as any).refreshTimeRange;
      if (range && !isInRefreshTimeRange(range)) return;
      this.refresh().catch(err => console.error('Failed to refresh LiveGeoJSONLayer:', err));
    }, interval);
  }

  /**
   * Stop auto-refresh timer
   */
  private stopAutoRefresh(): void {
    if (this.refreshHandle) {
      this.refreshHandle.cancel();
      this.refreshHandle = null;
    }
  }
}

/**
 * Default Mapbox layout properties for symbol layers.
 *
 * #549 — Fire Situation icons disappear at random because Mapbox suppresses
 * an entire icon+text symbol when its text label collides with another
 * layer's symbol. Defaults must opt every symbol layer into "always show":
 *
 *   - icon-allow-overlap / text-allow-overlap: true
 *       This layer's own icons/labels can overlap each other (don't suppress).
 *   - icon-ignore-placement / text-ignore-placement: true
 *       Other layers' placement engine doesn't suppress these.
 *   - icon-optional: true
 *       If text can't be placed, render the icon alone instead of hiding both.
 *
 * Per-layer DB config (style.layout) can still override any of these.
 *
 * Exported (with the __ForTests suffix) only for the unit test that locks
 * in the symptom-fix contract. Production code reads it via the
 * defaultLayout map inside buildLayerDefinition.
 */
export function __buildDefaultSymbolLayoutForTests(
  iconId: string | null,
  iconSize: number,
): Record<string, unknown> {
  return {
    visibility: 'visible',
    'icon-image': iconId || '',
    'icon-size': iconSize,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'icon-optional': true,
  };
}

/**
 * Default paint for symbol layers. #697: label text gets a white halo so
 * Fire IDs (and any other symbol-layer labels) stay legible when overlaid on
 * dark backgrounds — VIIRS hotspots, satellite imagery. Mirrors the established
 * VARLayer treatment (#ffffff / 1.5). A per-layer DB style.paint still overrides
 * (buildLayerDefinition merges style.paint over these defaults).
 *
 * Exported (with the __ForTests suffix) so the unit test can lock the halo
 * contract; production buildLayerDefinition consumes it directly.
 */
export function __buildDefaultSymbolPaintForTests(opacity: number): Record<string, unknown> {
  return {
    'icon-opacity': opacity,
    'text-opacity': opacity,
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.5,
  };
}

/** One coloured fire icon to register, per #653 status bucket. */
export interface FireIconRegistration {
  label: string;
  color: string;
  size: number;
  /** #697: white outline for the black Out icon only; 0 = no halo (active fires). */
  haloColor?: string;
  haloWidth: number;
  /** Subtle grey drop shadow on active fire icons; 0 = no shadow (Out icon). */
  shadowColor?: string;
  shadowBlur: number;
}

/**
 * #653: the coloured fire icons to register for a style — one per status
 * bucket, in the descriptor's order. Empty for non-fire / non-symbol layers.
 * onEnable loads each; the icon cache keys by (name, color, size) so every
 * bucket gets a distinct map image id.
 *
 * Exported (with the __ForTests suffix) so the unit test can lock the
 * registration contract; production onEnable reads it directly.
 */
export function __fireIconRegistrationsForTests(style: any): FireIconRegistration[] {
  const symbology = getSymbologyFromStyle(style);
  if (!symbology || symbology.iconName !== 'fire') return [];
  return symbology.categories.map((c) => ({
    label: c.label,
    color: c.color,
    size: c.size,
    ...(c.haloColor ? { haloColor: c.haloColor } : {}),
    haloWidth: c.haloWidth ?? 0,
    ...(c.shadowColor ? { shadowColor: c.shadowColor } : {}),
    shadowBlur: c.shadowBlur ?? 0,
  }));
}

/**
 * #653: build a fire symbol layer's layout, selecting the registered icon id
 * per status bucket (falling back to baseIconId for any bucket without a
 * registered icon). Carries the #549 always-show defaults. Production
 * buildSymbolLayout delegates here.
 */
export function __buildFireSymbolLayoutForTests(
  style: any,
  iconIdByLabel: Record<string, string>,
  baseIconId: string | null,
): Record<string, unknown> {
  const base = __buildDefaultSymbolLayoutForTests(baseIconId, style?.paint?.['icon-size'] || 1);
  const symbology = getSymbologyFromStyle(style);
  if (!symbology) return base;
  return {
    ...base,
    'icon-image': buildIconImageExpression(
      symbology,
      (cat) => iconIdByLabel[cat.label] ?? baseIconId ?? '',
    ),
    'icon-size': buildIconSizeExpression(symbology),
  };
}
